/**
 * Core Layer - Utils
 * Pure utility functions (framework-agnostic)
 */

import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

/**
 * Combines class names using clsx and tailwind-merge
 * Useful for conditionally applying tailwind classes
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Format a date using the Intl.DateTimeFormat API
 */
export function formatDate(
  date: Date | string | number,
  options: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "long",
    day: "numeric",
  },
  locale = "en"
) {
  return new Intl.DateTimeFormat(locale, options).format(
    typeof date === "string" || typeof date === "number" ? new Date(date) : date
  );
}

/**
 * Parse a JSON string safely without throwing
 */
export function safeJsonParse<T>(json: string, fallback: T): T {
  try {
    return JSON.parse(json) as T;
  } catch (error: unknown) {
    console.error(error);
    return fallback;
  }
}

/**
 * Truncate a string to a maximum length
 */
export function truncate(str: string, length: number): string {
  return str.length > length ? `${str.substring(0, length)}...` : str;
}

/**
 * Wait for a specified amount of time
 */
export function wait(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Generate a UUID
 */
export function uuid(): string {
  return crypto.randomUUID
    ? crypto.randomUUID()
    : Math.random().toString(36).substring(2, 15);
}

/**
 * Debounce a function
 */
export function debounce<T extends (...args: unknown[]) => void>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: ReturnType<typeof setTimeout> | null = null;

  return function (...args: Parameters<T>) {
    const later = () => {
      timeout = null;
      func(...args);
    };

    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

/**
 * Throttle a function
 */
export function throttle<T extends (...args: unknown[]) => void>(
  func: T,
  limit: number
): (...args: Parameters<T>) => void {
  let inThrottle: boolean = false;

  return function (...args: Parameters<T>) {
    if (!inThrottle) {
      func(...args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  };
}

/**
 * Deep clone an object
 */
export function deepClone<T>(obj: T): T {
  return JSON.parse(JSON.stringify(obj));
}

/**
 * Check if a value is empty (null, undefined, empty string, empty array, empty object)
 */
export function isEmpty(value: unknown): boolean {
  if (value === null || value === undefined) return true;
  if (typeof value === "string") return value.trim() === "";
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === "object") return Object.keys(value).length === 0;
  return false;
}

/**
 * Capitalize the first letter of a string
 */
export function capitalize(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

/**
 * Convert a string to kebab-case
 */
export function toKebabCase(str: string): string {
  return str
    .replace(/([a-z])([A-Z])/g, "$1-$2")
    .replace(/[\s_]+/g, "-")
    .toLowerCase();
}

/**
 * Convert a string to camelCase
 */
export function toCamelCase(str: string): string {
  return str
    .replace(/[-_\s]+(.)?/g, (_, char) => (char ? char.toUpperCase() : ""))
    .replace(/^(.)/, (char) => char.toLowerCase());
}

/**
 * Extract a human-readable error message from various error types
 * Handles Axios errors, standard Error objects, and unknown types
 *
 * @param error - The error to extract message from
 * @param fallback - Fallback message if extraction fails
 * @returns A human-readable error message
 */
export function extractErrorMessage(
  error: unknown,
  fallback = "An unexpected error occurred"
): string {
  if (!error) return fallback;

  // Handle Axios errors (check for response.data.detail or response.data.message)
  if (
    typeof error === "object" &&
    "response" in error &&
    error.response &&
    typeof error.response === "object"
  ) {
    const response = error.response as { data?: unknown };
    const data = response.data;

    if (data && typeof data === "object") {
      const dataObj = data as Record<string, unknown>;

      // FastAPI/Python style: { detail: "..." } or { detail: [...] }
      if (dataObj.detail) {
        if (typeof dataObj.detail === "string") {
          return dataObj.detail;
        }
        if (Array.isArray(dataObj.detail)) {
          const messages = dataObj.detail
            .map((e: { msg?: string; message?: string }) => e.msg || e.message)
            .filter(Boolean);
          if (messages.length > 0) return messages.join(", ");
        }
      }

      // Standard style: { message: "..." }
      if (typeof dataObj.message === "string") {
        return dataObj.message;
      }

      // Nested error object: { error: { message: "..." } }
      if (dataObj.error && typeof dataObj.error === "object") {
        const errObj = dataObj.error as { message?: string };
        if (errObj.message) return errObj.message;
      }
    }

    // Plain string response
    if (typeof data === "string") {
      return data;
    }
  }

  // Standard Error object
  if (error instanceof Error) {
    return error.message;
  }

  // String error
  if (typeof error === "string") {
    return error;
  }

  return fallback;
}
